<template>
  <d-modal 
    :active="active" 
    @close="close"
    title="Reset User Progress - All Content"
    width="500"
  >
    <div class="space-y-4">
      <div>
        <p class="text-gray-700 mb-4">
          This will reset ALL progress for a specific user in ALL content listings within the subject area 
          <strong>"{{ subjectArea.name }}"</strong>. This action cannot be undone.
        </p>
        
        <d-field 
          label="User Email" 
          :error="emailError"
          required
        >
          <d-input
            v-model="email"
            placeholder="Enter user email address"
            type="email"
            :error="!!emailError"
            @input="clearError"
          />
        </d-field>
        
        <div class="mt-4 p-3 bg-red-50 border border-red-200 rounded">
          <div class="flex items-start">
            <d-icon icon="exclamation-triangle" class="text-red-600 mt-0.5 mr-2" />
            <div class="text-sm text-red-800">
              <strong>Warning:</strong> This will remove ALL user interactions for ALL content in this subject area including:
              <ul class="list-disc list-inside mt-1 ml-4">
                <li>View progress for all content listings</li>
                <li>Quiz completions for all content</li>
                <li>Downloaded files for all content</li>
                <li>All completion status across all content</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex justify-end gap-3 pt-4 border-t border-gray-200 mt-6">
        <d-btn 
          flat 
          @click="close"
        >
          Cancel
        </d-btn>
        <d-btn 
          color="error" 
          @click="resetProgress"
          :loading="loading"
          :disabled="!email.trim()"
        >
          Reset All Progress
        </d-btn>
      </div>
    </div>

  </d-modal>
</template>

<script>
import axios from 'axios';
import { toast } from '@services/toast.service';

export default {
  name: 'ResetAllUserProgressModal',
  
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    subjectArea: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      email: '',
      emailError: '',
      loading: false,
    };
  },

  watch: {
    active(newVal) {
      if (newVal) {
        this.resetForm();
      }
    },
  },

  methods: {
    close() {
      this.$emit('close');
    },

    resetForm() {
      this.email = '';
      this.emailError = '';
      this.loading = false;
    },

    clearError() {
      this.emailError = '';
    },

    async resetProgress() {
      if (!this.email.trim()) {
        this.emailError = 'User email is required';
        return;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.email.trim())) {
        this.emailError = 'Please enter a valid email address';
        return;
      }

      // Validate user exists before showing confirmation
      this.loading = true;
      try {
        await axios.get(`/api/admin/users/${encodeURIComponent(this.email)}/validate`);
      } catch (error) {
        if (error.response?.status === 404) {
          this.emailError = 'User with this email not found';
          toast.error('User with this email address was not found');
        } else {
          this.emailError = 'Unable to validate user. Please try again.';
          toast.error('Unable to validate user. Please try again.');
        }
        this.loading = false;
        return;
      }
      this.loading = false;

      // Show confirmation dialog using native confirm
      const confirmed = confirm(
        `Are you sure you want to reset ALL progress for user "${this.email}" in ALL content within "${this.subjectArea.name}"?\n\nThis action cannot be undone and will affect ALL content listings in this subject area.`
      );
      
      if (confirmed) {
        await this.performReset();
      }
    },

    async performReset() {
      this.loading = true;
      try {
        await axios.delete(`/api/admin/subject-areas/${this.subjectArea.id}/users/${encodeURIComponent(this.email)}/interactions/reset-all`);
        
        toast.success(`All progress has been reset for user ${this.email} in ${this.subjectArea.name}`);
        this.close();
      } catch (error) {
        if (error.response?.status === 404) {
          this.emailError = 'User with this email not found or no progress to reset';
          toast.error('User with this email not found or no progress to reset');
        } else {
          if (error.response?.data?.message) {
            toast.error(error.response.data.message);
          } else if (error.response?.data) {
            toast.error(error.response.data);
          } else {
            toast.error('An error occurred while resetting user progress');
          }
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>